import { logger } from '@vagabondinternet/logging';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type IdentitySession, type SessionFailure, sessionFailure } from '../api';

export type HandoffState =
  /** Nothing in progress: show the form. */
  | { phase: 'idle' }
  /** Checking for a signed-in user, or exchanging their token for a session. */
  | { phase: 'working' }
  | { phase: 'failed'; failure: SessionFailure };

export interface SessionHandoff {
  state: HandoffState;
  /** Exchanges the signed-in player's fresh ID token for a session, then leaves with the URL the API returned. */
  complete: () => Promise<void>;
  /** Signs out of Firebase and goes back to the form. */
  startOver: () => Promise<void>;
}

export interface SessionHandoffOptions {
  session: IdentitySession;
  /** return_to as received; the API validates it. */
  returnTo: string | null;
  /** Leaves the identity app once the player has a session (useAuthNavigation's `leave`). */
  leave: (returnUrl: string | null) => void;
  /**
   * Silent sign-in: on mount, a verified player already signed in to Firebase is handed off without seeing the form.
   * It never retries by itself.
   */
  silent?: boolean;
  /**
   * Called, instead of failing, when the player still has to verify their address: silent sign-in found an
   * unverified user, or POST /session refused with EMAIL_NOT_VERIFIED. The handoff stays `working` meanwhile.
   * Without it, an unverified silent user sees the form and EMAIL_NOT_VERIFIED is a failure like any other.
   */
  onUnverified?: () => void;
}

/**
 * Hands a player signed in to Firebase over to a words-happen session. A failure never leaves as if signed in: it
 * puts the handoff in `failed`, for HandoffScreen to explain.
 */
export const useSessionHandoff = ({
  session,
  returnTo,
  leave,
  silent = false,
  onUnverified,
}: SessionHandoffOptions): SessionHandoff => {
  const [state, setState] = useState<HandoffState>(() =>
    silent && session.mayBeSignedIn() ? { phase: 'working' } : { phase: 'idle' },
  );
  // React StrictMode mounts twice in development; silent sign-in must still run once.
  const silentStarted = useRef(false);

  const complete = useCallback(async () => {
    setState({ phase: 'working' });
    try {
      const { returnUrl } = await session.exchange(await session.freshIdToken(), returnTo);
      // Stays working while the page leaves.
      leave(returnUrl);
    } catch (error) {
      const failure = sessionFailure(error);
      logger.error({ message: 'Session exchange failed', failure });
      if (failure === 'email-not-verified' && onUnverified) {
        onUnverified();
        return;
      }
      setState({ phase: 'failed', failure });
    }
  }, [session, returnTo, leave, onUnverified]);

  const startOver = useCallback(async () => {
    setState({ phase: 'working' });
    try {
      await session.signOut();
    } catch (error) {
      logger.warn({ message: 'Signing out of Firebase failed', failure: sessionFailure(error) });
    }
    setState({ phase: 'idle' });
  }, [session]);

  useEffect(() => {
    if (!silent || silentStarted.current || !session.mayBeSignedIn()) {
      return;
    }
    silentStarted.current = true;

    session
      .signedInUser()
      .then((user) => {
        if (user?.emailVerified) {
          return complete();
        }
        if (user && onUnverified) {
          return onUnverified();
        }
        setState({ phase: 'idle' });
      })
      .catch((error) => {
        const failure = sessionFailure(error);
        logger.error({ message: 'Silent sign-in failed', failure });
        setState({ phase: 'failed', failure });
      });
  }, [silent, session, complete, onUnverified]);

  return { state, complete, startOver };
};
